import React from 'react';
import './assessment-end-screenV1.scss';
import MeritApi from '../../../apis/merit-api';
import ChapterEloAssessmentReport from "../../student-report/student-report";
import ClassMockTestPerformanceReport from "../../class-mock-test-performance-report/class-mock-test-performance-report";
import SharedProfile from "../../../../shared-storage/profile";
import { Constants } from "../../../constants/constants";
import history from "../../routes/history";
import Path from "../../routes/routes-path";
import SharedThemeCategory from "../../../../shared-storage/category-theme";

export const CelebrationHeader = ({ chapterName, title = "Assessment completed" }) => (
    <div className="celebration-header">
        <div className="decorative-dots"></div>
        <div className="chapter-tag">{chapterName}</div>
        <h1>{title}</h1>
        <p>Great effort — every activity helps you grow.</p>
    </div>
);

export const MeritCard = ({ merits, bonusUnlocked }) => {
    const [displayMerits, setDisplayMerits] = React.useState(0);

    React.useEffect(() => {
        if (!merits) {
            setDisplayMerits(0);
            return;
        }

        let startTimestamp = null;
        const duration = 1500;

        const step = (timestamp) => {
            if (!startTimestamp) startTimestamp = timestamp;
            const progress = Math.min((timestamp - startTimestamp) / duration, 1);
            // easeOutQuad for faster, smooth deceleration
            const easeOut = 1 - Math.pow(1 - progress, 2);
            setDisplayMerits(Math.floor(easeOut * merits));

            if (progress < 1) {
                setDisplayMerits(Number((easeOut * merits).toFixed(1)));
                window.requestAnimationFrame(step);
            } else {
                setDisplayMerits(Number(merits.toFixed(2)));
            }
        };

        window.requestAnimationFrame(step);
    }, [merits]);

    return (
        <div className="cw-card merit-card">
            <div className="card-header">
                ★ MERITS EARNED
            </div>
            <div className="card-body">
                <div className="circle-tr"></div>
                <div className="circle-bl"></div>
                <div className="merit-number">+{displayMerits}</div>
                <div className="merit-label">MERITS</div>
                {bonusUnlocked && (
                    <div className="accuracy-pill">
                        <span>⚡</span> Accuracy bonus unlocked
                    </div>
                )}
            </div>
        </div>
    );
};

export const PerformanceCard = ({ score, accuracy, timeTaken, correctAnswers, incorrectAnswers }) => (
    <div className="cw-card performance-card">
        <div className="card-header">
            ▩ PERFORMANCE SUMMARY
        </div>
        <div className="card-body">
            <div className="stat-cell">
                <div className="stat-value">{score}</div>
                <div className="stat-label">Score</div>
            </div>
            <div className="stat-cell">
                <div className="stat-value">{accuracy}%</div>
                <div className="stat-label">Accuracy</div>
            </div>
            {timeTaken !== undefined && timeTaken !== null && (
                <div className="stat-cell">
                    <div className="stat-value">{timeTaken}</div>
                    <div className="stat-label">Time taken</div>
                </div>
            )}
            {correctAnswers !== undefined && correctAnswers !== null && (
                <div className="stat-cell">
                    <div className="stat-value correct-value">{correctAnswers}</div>
                    <div className="stat-label">Correct answers</div>
                </div>
            )}
            {incorrectAnswers !== undefined && incorrectAnswers !== null && (
                <div className="stat-cell">
                    <div className="stat-value incorrect-value" style={{ color: '#d9534f' }}>{incorrectAnswers}</div>
                    <div className="stat-label">Incorrect answers</div>
                </div>
            )}
        </div>
    </div>
);

export const BreakdownCard = ({ baseMerits, accuracyBonus, multiplier, activityTypeName, accuracy }) => {
    const total = baseMerits + accuracyBonus;
    return (
        <div className="cw-card breakdown-card">
            <div className="card-header">
                ✎ HOW YOUR MERITS WERE CALCULATED
            </div>
            <div className="card-body">
                <div className="breakdown-row">
                    <div className="row-label">Base Merits for {activityTypeName}</div>
                    <div className="row-value">+{baseMerits}</div>
                </div>
                <div className="breakdown-row">
                    <div className="row-label">Accuracy Bonus ({multiplier}X) for {accuracy}%</div>
                    <div className="row-value">+{accuracyBonus}</div>
                </div>
                <div className="breakdown-row total-row">
                    <div className="row-label">Total Merits Earned</div>
                    <div className="row-value">+{total}</div>
                </div>
                <div className="hint-box">
                    <span>ⓘ</span> Score above 80% to unlock the 1.35× multiplier next time
                </div>
            </div>
        </div>
    );
};

export const LeaderboardCard = ({ topThree, studentRank, studentMerits, studentName = "You", onViewFullLeaderboard }) => {
    const hasCurrentUserInTopThree = topThree.some(student => student.isCurrentUser);

    return (
        <div className="cw-card leaderboard-card">
            <div className="card-header">
                ⚡ CLASS LEADERBOARD — THIS MONTH
            </div>
            <div className="card-body">
                <div className="lb-header-row">
                    <div className="lb-rank-hdr">Rank</div>
                    <div className="lb-name-hdr">Name</div>
                    <div className="lb-merits-hdr">Merits</div>
                </div>

                {topThree.map((student, index) => {
                    const isCurrentUser = student.isCurrentUser;
                    let rankClass = 'lb-standard';
                    if (student.rank === 1) rankClass = 'lb-gold';
                    else if (student.rank === 2) rankClass = 'lb-silver';
                    else if (student.rank === 3) rankClass = 'lb-bronze';

                    const rowClass = `lb-row ${rankClass}${isCurrentUser ? ' lb-current-user' : ''}`;
                    return (
                        <div key={student.rank || index} className={rowClass}>
                            <div className="lb-rank-col">
                                <div className="lb-rank">{student.rank || (index + 1)}</div>
                            </div>
                            <div className="lb-name">{student.name}{isCurrentUser ? ' (You)' : ''}</div>
                            <div className="lb-merits">{student.merits}</div>
                        </div>
                    );
                })}

                {!hasCurrentUserInTopThree && (
                    <>
                        <div className="lb-row lb-standard lb-current-user">
                            <div className="lb-rank-col">
                                <div className="lb-rank">{studentRank}</div>
                            </div>
                            <div className="lb-name">{studentName} (You)</div>
                            <div className="lb-merits">{studentMerits}</div>
                        </div>
                    </>
                )}

                <div className="lb-view-full" onClick={onViewFullLeaderboard} style={{ cursor: "pointer" }}>
                    View full leaderboard →
                </div>
            </div>
        </div>
    );
};

// Main Component
export default class AssessmentCompletePageV1 extends React.Component {
    constructor(props) {
        super(props);

        // Store metaInfo fields as instance variables — mirrors the working V0 pattern
        const meta = this.props.metaInfo || {};
        this.meta = meta;
        this.chapterTitle = meta.chapterTitle || meta.chapterName || "Assessment";
        this.categoryThemeId = meta.categoryThemeId;
        this.assessmentActivityId = meta.assessmentActivityId;
        this.chapterKey = meta.chapterKey;

        this.theme = SharedThemeCategory.getThemeCategory(this.categoryThemeId);

        this.userInfo = SharedProfile.getProfile();

        // Initial empty/default state
        this.state = {
            chapterName: "",
            score: "0/0",
            accuracy: 0,
            timeTaken: "0 min",
            correctAnswers: 0,
            baseMerits: 0,
            accuracyBonus: 0,
            multiplier: 1.0,
            leaderboardData: [],
            studentRank: '-',
            studentMerits: 0,
            isLoading: true,
            showPerformanceReport: false
        };
    }

    componentDidMount() {
        this.loadRealData();
    }

    loadRealData() {
        const metaInfo = this.props.metaInfo || {};
        const data = metaInfo.data || {};

        const chapterName = metaInfo.chapterTitle || "Assessment";
        const score = `${data.markScored || 0}/${data.totalMark || 0}`;
        const accuracy = data.percentage || 0;
        const timeTaken = `${Math.ceil((data.timeTaken || 0) / 60)} min`;
        const correctAnswers = data.correct || 0;

        this.setState({
            chapterName,
            score,
            accuracy,
            timeTaken,
            correctAnswers
        });

        // Fetch merits data
        Promise.all([
            MeritApi.getStudentMerits(),
            MeritApi.getStudentMeritHistory({ limit: 1 }),
            MeritApi.getStudentMeritLeaderboard({ limit: 3, category: 'Top Performer', subjectId: this.userInfo ? this.userInfo.subjectId : undefined })
        ]).then(([meritsRes, historyRes, leaderboardRes]) => {
            const studentMerits = Number((meritsRes.data?.totalActivityMerits || 0).toFixed(2));
            const studentRank = meritsRes.data?.topPerformerRank ?? meritsRes.data?.topPerfromerRank ?? '-';

            // Extract latest activity merits
            let earnedMerits = 0; // fallback default
            let fetchedBaseMerits = 0;
            let fetchedMultiplier = 1.0;

            if (historyRes.data && historyRes.data.length > 0) {
                const latestHistory = historyRes.data[0];
                if (latestHistory && latestHistory.activityLevel) {
                    let acts = [];
                    Object.values(latestHistory.activityLevel).forEach(item => {
                        if (Array.isArray(item)) acts.push(...item);
                        else acts.push(item);
                    });
                    if (acts.length > 0) {
                        acts.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
                        const act = acts[0];
                        earnedMerits = act.activityMerits !== undefined ? act.activityMerits : earnedMerits;
                        fetchedBaseMerits = act.baseMerits !== undefined ? act.baseMerits : earnedMerits;
                        fetchedMultiplier = act.multiplier !== undefined ? act.multiplier : 1.0;
                    }
                }
            }

            const baseMerits = fetchedBaseMerits;
            const accuracyBonus = Number(Math.max(0, earnedMerits - fetchedBaseMerits).toFixed(2));
            const multiplier = fetchedMultiplier;

            let leaderboardData = [];
            const rawLeaderboard = leaderboardRes.data;
            if (rawLeaderboard && Array.isArray(rawLeaderboard)) {
                leaderboardData = rawLeaderboard.slice(0, 3).map((lb, index) => ({
                    rank: lb.rank || (index + 1),
                    initials: lb.name ? lb.name.substring(0, 2).toUpperCase() : "ST",
                    name: lb.name || "Student",
                    merits: Number((lb.finalMerits ?? lb.merits ?? 0).toFixed(2)),
                    isCurrentUser: lb.isCurrentUser || false
                }));
            }

            this.setState({
                baseMerits,
                accuracyBonus,
                multiplier,
                studentMerits,
                studentRank,
                leaderboardData,
                isLoading: false
            });
        }).catch(err => {
            console.error("Error fetching merit data", err);
            this.setState({ isLoading: false });
        });
    }

    handleViewAnswers = () => {
        const meta = this.meta || {};

        // Mirror V0: use instance variables set in constructor for reliability
        if (meta.isFromMyHomework && !meta.isDueDatePassed && meta.viewAnswerTime) {
            this.props.delegate && this.props.delegate.showViewAnswerTimerScreen &&
                this.props.delegate.showViewAnswerTimerScreen({
                    viewAnswerTime: meta.viewAnswerTime,
                    categoryThemeId: this.categoryThemeId,
                    from: meta.from === 'HOMEWORK' ? 'HOMEWORK' : 'CLASSWORK'
                });
            return;
        }

        const showAnswersFn = this.props.showViewAnswersScreen || this.props.showViewAnswerScreen;

        if (!showAnswersFn) {
            console.error('[V1 EndScreen] ERROR: showViewAnswerScreen/showViewAnswersScreen prop is not provided by parent!');
            return;
        }

        showAnswersFn({
            meta: {
                categoryThemeId: this.categoryThemeId,
                chapterTitle: this.chapterTitle,
                chapterKey: this.chapterKey,
                assessmentActivityId: this.assessmentActivityId,
                fromAssess: true,
                type: meta.type,
                from: meta.from === 'HOMEWORK' ? 'HOMEWORK' : 'CLASSWORK',
                isExamPrepKit: meta.isExamPrepKit || ""
            }
        });
    };

    handleViewReport = () => {
        this.setState({ showPerformanceReport: true });
    };

    handleViewLeaderboard = () => {
        history.push('/student/leaderboard');
    };

    hideChapterAssessmentReport() {
        this.setState({ showPerformanceReport: false });
    }

    goToHomeScreen = () => {
        if (this.props.delegate?.props?.history) {
            this.props.delegate.props.history.replace({ pathname: Path.HOME });
        } else {
            history.replace(Path.HOME);
        }
    }

    goToClassworkListScreen = () => {
        if (this.props.delegate && this.props.delegate.hideAssessmentEndScreen) {
            this.props.delegate.hideAssessmentEndScreen();
        }
    }

    renderBreadcrumb = () => {

        if (this.meta && this.meta.from == "HOMEWORK") {
            return (
                <div className='breadcrumb mt-2'>
                    <span className="home cursor-pointer" onClick={() => this.goToHomeScreen()}>{`Home >`}</span>
                    <span className='title-1 cursor-pointer pl-2' onClick={() => this.goToClassworkListScreen()}>{`My Homework >`}</span>
                    <span className='title-1 pl-2 active'>{`Assessment Completed`}</span>
                </div>
            )
        } else if (this.meta && this.meta.from == "CLASSWORK") {
            return (
                <div className='breadcrumb mt-2'>
                    <span className="home cursor-pointer" onClick={() => this.goToHomeScreen()}>{`Home >`}</span>
                    <span className='title-1 cursor-pointer pl-2' onClick={() => this.goToClassworkListScreen()}>{`School Assignments >`}</span>
                    <span className='title-1 pl-2 active'>{`Assessment Completed`}</span>
                </div>
            )
        }

        return (
            <div className='breadcrumb mt-2'>
                <span className="home cursor-pointer" onClick={() => this.goToHomeScreen()}>{`Home >`}</span>
                <span className='title-1 pl-2'>{`Assessment >`}</span>
                <span className='title-1 pl-2'>{`Start Assessment >`}</span>
                <span className='title-1 pl-2 active'>{`Assessment Completed`}</span>
            </div>
        )
    }

    render() {
        if (this.state.showPerformanceReport && this.userInfo && this.userInfo.class === 'Class 10' && this.userInfo.subjectId === Constants.MATHS_SUBJECTID) {
            return (
                <ClassMockTestPerformanceReport
                    meta={{
                        assessmentActivityId: this.assessmentActivityId,
                        from: this.meta.type ? this.meta.type : this.props.from,
                        chapterTitle: this.meta.chapterTitle ? this.meta.chapterTitle : '',
                        selectedMockTestTitle: 'Mock Test'
                    }}
                    delegate={this} />
            )
        } else if (this.state.showPerformanceReport) {
            // Mirror V0 exactly: read homework fields directly from this.props.metaInfo.homework
            // If homework object isn't present (e.g. from my-assessments.js), fall back to top-level metaInfo
            const metaInfo = this.props.metaInfo || {};
            const homework = metaInfo.homework || metaInfo;
            return (
                <ChapterEloAssessmentReport
                    delegate={this}
                    homeworkId={homework.homeworkId}
                    type={homework.type}
                    selectedItem={homework}
                    meta={{ assessmentActivityId: this.assessmentActivityId }} />
            );
        }

        const {
            chapterName, score, accuracy, timeTaken, correctAnswers,
            baseMerits, accuracyBonus, multiplier, leaderboardData,
            studentRank, studentMerits, isLoading
        } = this.state;

        let activityTypeName = "Assessment";
        if (this.meta && this.meta.from == "HOMEWORK") {
            activityTypeName = "Homework";
        } else if (this.meta && this.meta.from == "CLASSWORK") {
            activityTypeName = "School Assessment";
        } else if (this.meta && (this.meta.from == "CLASS_MOCK_TEST" || this.meta.from == "SCIENCE_CLASS_MOCK_TEST" || this.meta.from == "MOCK_TEST" || this.meta.from == "SELECTED_CHAPTER_MOCK_TEST" || this.meta.from == "SELECTED_CHAPTER_SCIENCE_CLASS_MOCK_TEST")) {
            activityTypeName = "Self Assessment";
        }

        return (
            <div className="assessment-complete-v1">
                {this.renderBreadcrumb()}
                <CelebrationHeader chapterName={chapterName} />

                <div className="two-column-grid">
                    <MeritCard
                        merits={baseMerits + accuracyBonus}
                        bonusUnlocked={accuracyBonus > 0}
                    />
                    <PerformanceCard
                        score={score}
                        accuracy={accuracy}
                        timeTaken={timeTaken}
                        correctAnswers={correctAnswers}
                    />
                </div>

                <div className="two-column-grid">
                    <BreakdownCard
                        baseMerits={baseMerits}
                        accuracyBonus={accuracyBonus}
                        multiplier={multiplier}
                        activityTypeName={activityTypeName}
                        accuracy={accuracy}
                    />
                    <LeaderboardCard
                        topThree={leaderboardData}
                        studentRank={studentRank}
                        studentMerits={studentMerits}
                        studentName={this.userInfo ? this.userInfo.name : "You"}
                        onViewFullLeaderboard={this.handleViewLeaderboard}
                    />
                </div>

                <div className="action-buttons" style={{ position: "relative", zIndex: 9999 }}>
                    <div className="btn-secondary" onClick={this.handleViewAnswers} style={{ cursor: "pointer", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: this.theme['themeColor'], color: '#fff', border: 'none' }}>
                        View answers
                    </div>
                    <div className="btn-secondary" onClick={this.handleViewReport} style={{ cursor: "pointer", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: this.theme['themeColor'], color: '#fff', border: 'none' }}>
                        View report
                    </div>
                </div>
            </div>
        );
    }
}
